import { Component } from '@theme/component';

const STORAGE_KEY = 'pc-quote-rows';
const MIN_QUERY_LENGTH = 2;
/** The most the search asks for when a chip filters its results in the browser (the search itself returns up to 10). */
const FILTERED_RESULTS = 10;
const SEARCH_DELAY = 250;
const MAX_DOZENS = 999;

/**
 * @typedef {object} Variant
 * @property {number} id
 * @property {string} title - Empty for products without options.
 * @property {boolean} available
 *
 * @typedef {object} Product
 * @property {number} id
 * @property {string} title
 * @property {string} sku
 * @property {string} url
 * @property {string} image
 * @property {string} type
 * @property {string} cutLabel
 * @property {string} state - "in_stock", "on_demand" or "sold_out".
 * @property {string} stateLabel
 * @property {Variant[]} variants
 *
 * @typedef {object} Row
 * @property {Product} product
 * @property {Record<string, number>} doz - Dozens per variant id.
 *
 * @typedef {{ state: string, type: string }} Filter - The availability ("in_stock", "on_demand" or "") and the protection
 *   type ("Corte", "Frío"... or "") the chips filter by; both apply at once.
 */

/**
 * Quote builder of the quote page (`sections/pc-cotizador.liquid`).
 *
 * We ship by the dozen, one size per dozen, from a minimum of dozens per model (3 by default, subject to availability).
 * Each model of the list is a row with a dozen counter per size, its pairs, a status and a bar with a
 * mark where the minimum is. The picker (Predictive
 * Search rendered by `sections/pc-cotizador-results.liquid`) adds models: opened without a query it offers the suggested
 * collection, the chips filter by availability and protection type (the same two filters as the catalog), in the browser.
 * A model below the minimum blocks the send button.
 * The list is written as "SKU · size × dozens (pairs); …; Total" in the form's "productos" field: the theme's contact
 * form (sent without reloading) or a Shopify Forms app block, whose "Productos" field is found and filled here
 * (see docs/forms.md).
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} strings - JSON with the translated texts.
 * @property {HTMLElement} picker - Search field, panel and live region.
 * @property {HTMLInputElement} search - Search combobox.
 * @property {HTMLElement} panel - Chips and results.
 * @property {HTMLElement} chips - Filter chips.
 * @property {HTMLElement} heading - Title of the results.
 * @property {HTMLElement} results - Listbox with the options.
 * @property {HTMLElement} more - "Showing N results" hint.
 * @property {HTMLElement} status - Live region (results count, added model).
 * @property {HTMLElement} list - Models of the quote.
 * @property {HTMLElement} empty - Hint shown while the list is empty.
 * @property {HTMLElement} models - Models count.
 * @property {HTMLElement} total - Total dozens and pairs.
 * @property {HTMLElement} blocked - Message shown while a model is below the minimum.
 * @property {HTMLTemplateElement} rowTemplate - Markup of a model.
 * @property {HTMLTemplateElement} sizeTemplate - Markup of a size of a model.
 * @property {HTMLInputElement[]} [products] - Hidden fields of the native form.
 * @property {HTMLButtonElement} [submit] - Native form submit button.
 * @property {HTMLElement} [error] - Native form error message.
 * @property {HTMLElement} [success] - Native form success message.
 * @property {HTMLElement} [content] - Native form fields.
 * @property {HTMLAnchorElement} [again] - "Send another request" link.
 * @property {HTMLElement} [app] - Container of the Shopify Forms app block.
 * @property {HTMLTemplateElement} [appStyles] - Rules adopted into the shadow roots of the Shopify Forms block.
 *
 * @extends {Component<Refs>}
 */
class PcQuote extends Component {
  requiredRefs = [
    'strings',
    'picker',
    'search',
    'panel',
    'chips',
    'heading',
    'results',
    'more',
    'status',
    'list',
    'empty',
    'models',
    'total',
    'blocked',
    'rowTemplate',
    'sizeTemplate',
  ];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {AbortController | undefined} */
  #searchAbort;

  /** @type {number | undefined} */
  #timer;

  /** Identifies the latest picker request, so an older answer never replaces a newer one. */
  #requestId = 0;

  /** @type {MutationObserver | undefined} */
  #appObserver;

  /** Observers of the open shadow roots inside the Shopify Forms block (the form is drawn inside one). */
  #shadowObservers = new Map();

  /**
   * The theme's layout rules for the Forms shadow roots, shared by all of them.
   * @type {CSSStyleSheet | undefined}
   */
  #appSheet;

  /** @type {number | undefined} */
  #appPoll;

  /** @type {Record<string, string>} */
  #strings = {};

  /** @type {Row[]} */
  #rows = [];

  /** @type {HTMLElement[]} */
  #options = [];

  #activeIndex = -1;

  /** @type {Filter} */
  #filter = { state: '', type: '' };

  /** Options of a collection (the suggested one, the whole catalog), as the server rendered them, by URL. */
  #browseHtml = new Map();

  /** Set while the script moves the focus to the search field, which must not open the panel. */
  #skipOpen = false;

  get #pairsPerDozen() {
    return Number(this.dataset.pairsPerDozen) || 12;
  }

  get #minDozens() {
    return Number(this.dataset.minDozens) || 3;
  }

  /** Dozens that fill the bar (12 dozens = 144 pairs by default), whatever the minimum is. */
  get #boxDozens() {
    return Number(this.dataset.boxDozens) || 12;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#strings = JSON.parse(this.refs.strings.textContent || '{}');

    const { picker, search, panel, chips, results, list } = this.refs;
    search.addEventListener('input', this.#onSearchInput, { signal });
    search.addEventListener('keydown', this.#onSearchKeydown, { signal });
    search.addEventListener('focus', this.#onSearchFocus, { signal });
    search.addEventListener('click', this.#onSearchFocus, { signal });
    picker.addEventListener('keydown', this.#onPickerKeydown, { signal });
    picker.addEventListener('focusout', this.#onPickerFocusOut, { signal });
    // Keep the focus where it is while the panel is pressed
    panel.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
    chips.addEventListener('click', this.#onChipClick, { signal });
    results.addEventListener('click', this.#onResultClick, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    list.addEventListener('input', this.#onListInput, { signal });
    list.addEventListener('change', this.#onListChange, { signal });
    list.addEventListener('click', this.#onListClick, { signal });
    list.addEventListener('keydown', this.#onListKeydown, { signal });

    this.querySelector('form.pc-quote__form')?.addEventListener('submit', this.#onSubmit, { signal });
    this.refs.again?.addEventListener('click', this.#onAgainClick, { signal });

    if (this.refs.app) {
      this.#appObserver = new MutationObserver(() => this.#syncProducts());
      this.#appObserver.observe(this.refs.app, { childList: true, subtree: true });

      // The form draws itself once the app registers its element: style its shadow root as soon as that happens
      customElements.whenDefined('shopify-forms-embed').then(() => {
        if (this.isConnected) this.#syncProducts();
      });

      // A shadow root attached after the element was added raises no mutation: look again while the form loads
      let attempts = 0;
      this.#appPoll = window.setInterval(() => {
        this.#syncProducts();
        if (++attempts >= 40) window.clearInterval(this.#appPoll);
      }, 500);
    }

    void this.#restore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
    this.#searchAbort?.abort();
    window.clearTimeout(this.#timer);
    this.#appObserver?.disconnect();
    this.#appObserver = undefined;
    window.clearInterval(this.#appPoll);
    for (const observer of this.#shadowObservers.values()) observer.disconnect();
    this.#shadowObservers.clear();
  }

  // ---------------------------------------------------------------- numbers and texts

  /**
   * @param {string} template
   * @param {Record<string, string | number>} values
   */
  #fill(template, values) {
    return template.replace(/__(\w+)__/g, (match, key) => (key in values ? String(values[key]) : match));
  }

  /** @param {number} count */
  #dozensText(count) {
    return this.#fill(count === 1 ? this.#strings.dozensOne : this.#strings.dozensOther, { qty: count });
  }

  /** @param {number} dozens */
  #pairsText(dozens) {
    return this.#fill(this.#strings.pairs, { qty: dozens * this.#pairsPerDozen });
  }

  /** @param {Row} row */
  #dozensOf(row) {
    return Object.values(row.doz).reduce((sum, count) => sum + count, 0);
  }

  /** @param {Variant} variant */
  #sizeName(variant) {
    return variant.title || this.#strings.singleSize;
  }

  /** @param {Row} row */
  #statusOf(row) {
    const total = this.#dozensOf(row);
    if (total < this.#minDozens) return this.#fill(this.#strings.statusMissing, { qty: this.#minDozens - total });
    return this.#strings.statusMin;
  }

  // ---------------------------------------------------------------- picker

  #onSearchFocus = () => {
    if (this.#skipOpen || !this.refs.panel.hidden) return;
    void this.#refreshPicker();
  };

  #onSearchInput = () => {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#refreshPicker(), SEARCH_DELAY);
  };

  /** @param {Event} event */
  #onChipClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('button[data-filter]') : null;
    if (!(chip instanceof HTMLButtonElement)) return;

    // Each group of chips ("availability" or "type") sets its own part of the filter; the group's first chip clears it
    const group = chip.dataset.filter === 'type' ? 'type' : 'state';
    this.#filter = { ...this.#filter, [group]: chip.dataset.value ?? '' };
    for (const other of chip.closest('.pc-quote-picker__chips')?.querySelectorAll('button[data-filter]') ?? []) {
      other.setAttribute('aria-pressed', String(other === chip));
    }

    void this.#refreshPicker();
  };

  /**
   * Shows the options that match the query and the chips: the search of the shop for a query, the whole catalog when a
   * chip filters without a query (as the catalog filter does), the suggested collection when there is neither.
   */
  async #refreshPicker() {
    const { search, panel } = this.refs;
    const query = search.value.trim();
    const { state, type } = this.#filter;
    const filtering = Boolean(state || type);
    const requestId = ++this.#requestId;

    panel.hidden = false;
    search.setAttribute('aria-expanded', 'true');

    const terms = query.length >= MIN_QUERY_LENGTH ? query : '';

    try {
      const catalog = !terms && filtering && Boolean(this.dataset.catalogUrl);
      const browsing = !terms && !catalog && Boolean(this.dataset.suggestedUrl);
      const limit = filtering ? FILTERED_RESULTS : this.#maxResults;
      let options = terms
        ? await this.#fetchOptions(terms, limit)
        : catalog
          ? await this.#fetchBrowse(this.dataset.catalogUrl)
          : browsing
            ? await this.#fetchBrowse(this.dataset.suggestedUrl)
            : [];
      if (requestId !== this.#requestId) return;

      // Each result carries how the model is offered (see snippets/pc-availability-offer.liquid) and its protection types
      if (state) options = options.filter((option) => option.dataset.state === state);
      if (type) options = options.filter((option) => (option.dataset.types ?? '').split('|').includes(type));
      this.#showOptions(options, { browsing, limited: !browsing && !catalog && options.length >= this.#maxResults });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (requestId === this.#requestId) this.refs.status.textContent = this.#strings.searchError;
    }
  }

  get #maxResults() {
    return Number(this.dataset.maxResults) || 8;
  }

  /**
   * @param {string} terms
   * @param {number} [limit]
   * @param {boolean} [shared] - False for a request that a newer picker request must not cancel (the cart link).
   * @returns {Promise<HTMLElement[]>} The result options.
   */
  async #fetchOptions(terms, limit = this.#maxResults, shared = true) {
    const url = new URL(this.dataset.searchUrl ?? '/search/suggest', window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(limit));
    url.searchParams.set('resources[limit_scope]', 'all');
    url.searchParams.set('section_id', this.dataset.resultsSection ?? 'pc-cotizador-results');

    return this.#parseOptions(await this.#fetchText(url, shared));
  }

  /**
   * @param {string | undefined} collectionUrl
   * @returns {Promise<HTMLElement[]>} The models of a collection.
   */
  async #fetchBrowse(collectionUrl) {
    const key = collectionUrl ?? '';
    let html = this.#browseHtml.get(key);
    if (html === undefined) {
      const url = new URL(key, window.location.origin);
      url.searchParams.set('section_id', this.dataset.resultsSection ?? 'pc-cotizador-results');
      html = await this.#fetchText(url);
      this.#browseHtml.set(key, html);
    }

    return this.#parseOptions(html);
  }

  /**
   * @param {URL} url
   * @param {boolean} [shared] - Joins the picker's single in-flight request: a newer one cancels it.
   */
  async #fetchText(url, shared = true) {
    let signal = this.#abort?.signal;
    if (shared) {
      this.#searchAbort?.abort();
      this.#searchAbort = new AbortController();
      signal = this.#searchAbort.signal;
    }

    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(String(response.status));
    return response.text();
  }

  /**
   * @param {string} text
   * @returns {HTMLElement[]}
   */
  #parseOptions(text) {
    const html = new DOMParser().parseFromString(text, 'text/html');
    return /** @type {HTMLElement[]} */ ([...html.querySelectorAll('[data-pc-quote-results] [role="option"]')]);
  }

  /**
   * @param {HTMLElement[]} options
   * @param {{ browsing: boolean, limited: boolean }} state
   */
  #showOptions(options, { browsing, limited }) {
    const { results, search, status, heading, more } = this.refs;
    this.#options = options;
    this.#activeIndex = -1;

    if (options.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'pc-quote-option pc-quote-option--empty';
      empty.setAttribute('role', 'presentation');
      empty.textContent = this.#strings.noResults;
      results.replaceChildren(empty);
      status.textContent = this.#strings.noResults;
      heading.hidden = true;
    } else {
      results.replaceChildren(...options);
      const count = this.#fill(options.length === 1 ? this.#strings.resultsOne : this.#strings.resultsOther, {
        qty: options.length,
      });
      heading.textContent = browsing ? this.#strings.bestSellers : count;
      heading.hidden = false;
      status.textContent = count;
    }

    more.hidden = !limited;
    more.textContent = limited ? this.#fill(this.#strings.showing, { shown: options.length }) : '';

    this.#markListed();
    search.setAttribute('aria-expanded', 'true');
    search.removeAttribute('aria-activedescendant');
  }

  /** Writes "+" or "In list" on every option, according to the models already in the quote. */
  #markListed() {
    for (const option of this.#options) {
      const action = option.querySelector('.pc-quote-option__action');
      if (!action) continue;

      const listed = this.#rows.some((row) => row.product.id === this.#readProduct(option)?.id);
      option.toggleAttribute('data-listed', listed);
      action.replaceChildren();
      if (listed) {
        action.textContent = this.#strings.actionListed;
      } else {
        const plus = document.createElement('span');
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = this.#strings.actionAdd;
        action.append(plus, label);
      }
    }
  }

  #closePanel() {
    window.clearTimeout(this.#timer);
    this.#requestId++;
    this.#searchAbort?.abort();
    const { panel, search, results } = this.refs;
    panel.hidden = true;
    results.replaceChildren();
    this.#options = [];
    this.#activeIndex = -1;
    search.setAttribute('aria-expanded', 'false');
    search.removeAttribute('aria-activedescendant');
  }

  #focusSearch() {
    this.#skipOpen = true;
    this.refs.search.focus();
    this.#skipOpen = false;
  }

  /** @param {number} index */
  #setActive(index) {
    if (this.#options.length === 0) return;

    const next = (index + this.#options.length) % this.#options.length;
    this.#options.forEach((option, position) => {
      const active = position === next;
      option.toggleAttribute('data-active', active);
      option.setAttribute('aria-selected', String(active));
    });

    this.#activeIndex = next;
    const option = this.#options[next];
    this.refs.search.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  /** @param {KeyboardEvent} event */
  #onSearchKeydown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.refs.panel.hidden) void this.#refreshPicker();
        else this.#setActive(this.#activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#setActive(this.#activeIndex < 0 ? this.#options.length - 1 : this.#activeIndex - 1);
        break;
      case 'Enter':
        // Never send the form from the search field
        event.preventDefault();
        if (this.#activeIndex >= 0) this.#choose(this.#options[this.#activeIndex]);
        else if (this.#options.length === 1) this.#choose(this.#options[0]);
        break;
    }
  };

  /** @param {KeyboardEvent} event */
  #onPickerKeydown = (event) => {
    if (event.key !== 'Escape' || this.refs.panel.hidden) return;

    event.preventDefault();
    this.#closePanel();
    this.#focusSearch();
  };

  /** @param {FocusEvent} event */
  #onPickerFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.refs.picker.contains(event.relatedTarget)) return;
    this.#closePanel();
  };

  /** @param {Event} event */
  #onDocumentClick = (event) => {
    if (event.target instanceof Node && !this.refs.picker.contains(event.target)) this.#closePanel();
  };

  /** @param {Event} event */
  #onResultClick = (event) => {
    const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
    if (option instanceof HTMLElement) this.#choose(option);
  };

  /** @param {HTMLElement} option */
  #choose(option) {
    const product = this.#readProduct(option);
    if (!product) return;

    if (this.#rows.some((row) => row.product.id === product.id)) {
      this.refs.status.textContent = this.#fill(this.#strings.alreadyListed, { name: product.title });
      return;
    }

    this.#addRow(product);
    this.#markListed();
    this.refs.status.textContent = this.#fill(this.#strings.added, { name: product.title });
  }

  /**
   * @param {HTMLElement} option
   * @returns {Product | undefined}
   */
  #readProduct(option) {
    try {
      return JSON.parse(option.dataset.product ?? '');
    } catch {
      return undefined;
    }
  }

  // ---------------------------------------------------------------- list

  /**
   * Adds a model with the minimum order on one size, the middle one unless a size is given. A model that is already in
   * the list gets the dozens on that size.
   * @param {Product} product
   * @param {{ dozens?: number, size?: string }} [options]
   */
  #addRow(product, { dozens = this.#minDozens, size = '' } = {}) {
    const variants = product.variants;
    if (variants.length === 0) return;

    const wanted = size ? variants.find((variant) => variant.title.toLowerCase() === size.toLowerCase()) : undefined;
    const variant = wanted ?? variants[Math.floor(variants.length / 2)];

    let row = this.#rows.find((candidate) => candidate.product.id === product.id);
    if (!row) {
      row = { product, doz: {} };
      this.#rows.push(row);
    }
    row.doz[variant.id] = Math.min(MAX_DOZENS, (row.doz[variant.id] ?? 0) + dozens);

    this.#renderList();
  }

  /** Rebuilds the list from the state. Only called when models are added or removed, so typing keeps its focus. */
  #renderList() {
    const { list, empty } = this.refs;
    list.replaceChildren(...this.#rows.map((row) => this.#buildRow(row)));
    empty.hidden = this.#rows.length > 0;
    this.#update();
  }

  /** @param {Row} row */
  #buildRow(row) {
    const { product } = row;
    const el = /** @type {HTMLElement} */ (this.refs.rowTemplate.content.firstElementChild?.cloneNode(true));
    el.dataset.product = String(product.id);

    const image = el.querySelector('img');
    if (image instanceof HTMLImageElement && product.image) image.src = product.image;
    else el.querySelector('.pc-quote-row__thumb')?.setAttribute('hidden', '');

    const name = el.querySelector('.pc-quote-row__name');
    if (name instanceof HTMLAnchorElement) {
      name.textContent = product.title;
      name.href = product.url;
    }

    const chips = el.querySelector('.pc-quote-row__chips');
    if (chips) {
      if (product.sku) {
        const sku = document.createElement('span');
        sku.className = 'pc-quote-option__sku';
        sku.textContent = product.sku;
        chips.append(sku);
      }
      if (product.cutLabel) chips.append(this.#chip(`pc-quote-chip--cut`, product.cutLabel));
      chips.append(this.#chip(`pc-quote-chip--${product.state}`, product.stateLabel));
    }

    el.querySelector('[data-remove]')?.setAttribute(
      'aria-label',
      this.#fill(this.#strings.removeNamed, { name: product.title })
    );

    const sizes = el.querySelector('.pc-quote-row__sizes');
    for (const variant of product.variants) {
      const cell = /** @type {HTMLElement} */ (this.refs.sizeTemplate.content.firstElementChild?.cloneNode(true));
      cell.dataset.variant = String(variant.id);

      const label = this.#sizeName(variant);
      const nameEl = cell.querySelector('.pc-quote-size__name');
      if (nameEl) nameEl.textContent = variant.title ? this.#fill(this.#strings.size, { size: variant.title }) : label;

      const values = { size: label, name: product.title };
      cell.querySelector('[data-step="-1"]')?.setAttribute('aria-label', this.#fill(this.#strings.decrease, values));
      cell.querySelector('[data-step="1"]')?.setAttribute('aria-label', this.#fill(this.#strings.increase, values));
      const input = cell.querySelector('.pc-quote-size__input');
      if (input instanceof HTMLInputElement) {
        input.value = String(row.doz[variant.id] ?? 0);
        input.setAttribute('aria-label', this.#fill(this.#strings.inputLabel, values));
      }

      sizes?.append(cell);
    }

    const min = el.querySelector('[data-scale="min"]');
    if (min) min.textContent = this.#fill(this.#strings.scaleMin, { qty: this.#minDozens });
    el.style.setProperty('--pc-quote-mark', `${(this.#minDozens / this.#boxDozens) * 100}%`);

    return el;
  }

  /**
   * @param {string} modifier
   * @param {string} text
   */
  #chip(modifier, text) {
    const chip = document.createElement('span');
    chip.className = `pc-quote-chip ${modifier}`;
    chip.textContent = text;
    return chip;
  }

  /** Writes the state into the rows, the totals, the send button and the form field. */
  #update() {
    const { list } = this.refs;
    let totalDozens = 0;
    let blocked = 0;

    this.#rows.forEach((row, index) => {
      const el = list.children[index];
      const dozens = this.#dozensOf(row);
      const ok = dozens >= this.#minDozens;
      totalDozens += dozens;
      if (!ok) blocked++;
      if (el instanceof HTMLElement) this.#updateRow(el, row, dozens, ok);
    });

    const { models, total, blocked: blockedEl, submit } = this.refs;
    models.textContent = this.#rows.length
      ? this.#fill(this.#rows.length === 1 ? this.#strings.modelsOne : this.#strings.modelsOther, {
          qty: this.#rows.length,
        })
      : '';
    total.textContent = this.#rows.length
      ? this.#fill(this.#strings.lineTotal, { doz: totalDozens, pairs: totalDozens * this.#pairsPerDozen })
      : '';

    blockedEl.hidden = blocked === 0;
    blockedEl.textContent =
      blocked === 0
        ? ''
        : blocked === 1
          ? this.#fill(this.#strings.blockedOne, { min: this.#minDozens })
          : this.#fill(this.#strings.blockedOther, { qty: blocked, min: this.#minDozens });
    submit?.setAttribute('aria-disabled', String(blocked > 0));

    this.#persist();
    this.#syncProducts();
  }

  /**
   * @param {HTMLElement} el
   * @param {Row} row
   * @param {number} dozens
   * @param {boolean} ok
   */
  #updateRow(el, row, dozens, ok) {
    el.toggleAttribute('data-ok', ok);

    for (const cell of el.querySelectorAll('.pc-quote-size')) {
      if (!(cell instanceof HTMLElement)) continue;
      const count = row.doz[cell.dataset.variant ?? ''] ?? 0;
      cell.toggleAttribute('data-active', count > 0);
      const pairs = cell.querySelector('.pc-quote-size__pairs');
      if (pairs) pairs.textContent = count > 0 ? this.#pairsText(count) : this.#strings.noPairs;
    }

    const totalEl = el.querySelector('.pc-quote-row__total');
    if (totalEl) {
      const strong = document.createElement('strong');
      strong.textContent = this.#dozensText(dozens);
      totalEl.replaceChildren(strong, ` · ${this.#pairsText(dozens)}`);
    }

    const status = el.querySelector('.pc-quote-row__status');
    if (status) status.textContent = this.#statusOf(row);

    el.style.setProperty('--pc-quote-fill', `${Math.min(100, (dozens / this.#boxDozens) * 100)}%`);
  }

  /**
   * @param {Event} event
   * @returns {{ row: Row, el: HTMLElement, cell?: HTMLElement } | undefined}
   */
  #rowOf(event) {
    const target = event.target instanceof Element ? event.target : null;
    const el = target?.closest('.pc-quote-row');
    if (!(el instanceof HTMLElement)) return undefined;

    const row = this.#rows.find((candidate) => String(candidate.product.id) === el.dataset.product);
    const cell = target?.closest('.pc-quote-size');
    return row ? { row, el, cell: cell instanceof HTMLElement ? cell : undefined } : undefined;
  }

  /** Typing a count updates the totals without rebuilding the list. @param {Event} event */
  #onListInput = (event) => {
    const found = this.#rowOf(event);
    const input = event.target;
    if (!found?.cell || !(input instanceof HTMLInputElement)) return;

    const digits = input.value.replace(/\D/g, '');
    if (digits !== input.value) input.value = digits;
    found.row.doz[found.cell.dataset.variant ?? ''] = Math.min(MAX_DOZENS, parseInt(digits, 10) || 0);
    this.#update();
  };

  /** Leaving the field with an empty or odd value shows the count that was kept. @param {Event} event */
  #onListChange = (event) => {
    const found = this.#rowOf(event);
    const input = event.target;
    if (!found?.cell || !(input instanceof HTMLInputElement)) return;

    input.value = String(found.row.doz[found.cell.dataset.variant ?? ''] ?? 0);
  };

  /** @param {KeyboardEvent} event */
  #onListKeydown = (event) => {
    // The counts live inside the form: Enter in one of them must not send it
    if (event.key === 'Enter' && event.target instanceof HTMLInputElement) event.preventDefault();
  };

  /** @param {Event} event */
  #onListClick = (event) => {
    const found = this.#rowOf(event);
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!found || !button) return;

    if (button.hasAttribute('data-remove')) {
      this.#rows = this.#rows.filter((row) => row !== found.row);
      this.#renderList();
      this.#markListed();
      this.#focusSearch();
    } else if (button.dataset.step && found.cell) {
      const key = found.cell.dataset.variant ?? '';
      const next = Math.min(MAX_DOZENS, Math.max(0, (found.row.doz[key] ?? 0) + Number(button.dataset.step)));
      found.row.doz[key] = next;

      const input = found.cell.querySelector('.pc-quote-size__input');
      if (input instanceof HTMLInputElement) input.value = String(next);
      this.#update();
    }
  };

  // ---------------------------------------------------------------- form field

  /** @returns {string} "SKU · size × dozens (pairs); …; Total: dozens (pairs)" */
  #productsText() {
    const lines = [];
    let totalDozens = 0;

    for (const { product, doz } of this.#rows) {
      for (const variant of product.variants) {
        const dozens = doz[variant.id] ?? 0;
        if (dozens <= 0) continue;

        totalDozens += dozens;
        const values = {
          sku: product.sku || product.title,
          size: variant.title,
          doz: dozens,
          pairs: dozens * this.#pairsPerDozen,
        };
        lines.push(this.#fill(variant.title ? this.#strings.lineSized : this.#strings.lineSingle, values));
      }
    }

    if (lines.length === 0) return '';
    lines.push(this.#fill(this.#strings.lineTotal, { doz: totalDozens, pairs: totalDozens * this.#pairsPerDozen }));
    return lines.join('; ');
  }

  /** Fills the "productos" field of the native form and of the Shopify Forms block. */
  #syncProducts() {
    const text = this.#productsText();
    for (const field of this.refs.products ?? []) field.value = text;

    if (this.refs.app) this.#fillApp(text);
  }

  /**
   * Open shadow roots inside the Shopify Forms block, at any depth. The form is rendered inside a
   * `<shopify-forms-embed>` element with an open shadow root, so its fields are not reachable from the light DOM.
   * Each new root gets an observer, because the form draws and redraws itself after the page has loaded.
   * @returns {(Element | ShadowRoot)[]} The app container and every shadow root found inside it.
   */
  #appRoots() {
    const { app } = this.refs;
    if (!app) return [];

    /** @type {(Element | ShadowRoot)[]} */
    const roots = [app];
    for (let i = 0; i < roots.length; i++) {
      for (const element of roots[i].querySelectorAll('*')) {
        const shadow = element.shadowRoot;
        if (shadow && !roots.includes(shadow)) roots.push(shadow);
      }
    }

    for (const root of roots) {
      if (root instanceof ShadowRoot) this.#styleRoot(root);

      if (root instanceof ShadowRoot && !this.#shadowObservers.has(root)) {
        const observer = new MutationObserver(() => this.#syncProducts());
        observer.observe(root, { childList: true, subtree: true });
        this.#shadowObservers.set(root, observer);
      }
    }

    return roots;
  }

  /**
   * Gives a shadow root of the Shopify Forms block the theme's layout. The app draws the form, so the stylesheets of the
   * page do not reach it: the rules come from `snippets/pc-forms-embed-styles.liquid`. The app replaces its own adopted
   * sheets when it changes step, so this checks on every pass that the rules are still there and adds them back if not.
   * @param {ShadowRoot} root
   */
  #styleRoot(root) {
    const css = this.refs.appStyles?.content.querySelector('style')?.textContent;
    if (!css) return;

    try {
      if (!this.#appSheet) {
        this.#appSheet = new CSSStyleSheet();
        this.#appSheet.replaceSync(css);
      }
      if (!root.adoptedStyleSheets.includes(this.#appSheet)) {
        root.adoptedStyleSheets = [...root.adoptedStyleSheets, this.#appSheet];
      }
    } catch {
      // Browsers without constructable stylesheets
      if (root.querySelector('style[data-pc-forms]')) return;
      const style = document.createElement('style');
      style.dataset.pcForms = '';
      style.textContent = css;
      root.append(style);
    }
  }

  /**
   * Finds the "Productos" field inside the Shopify Forms block (by name, id, aria-label, placeholder or label text)
   * and writes the value the way a user would, so the form's own state sees it. Forms names its custom fields
   * `custom#<name>`, so the field created as "Productos" is `custom#productos`. The app's send button is turned off
   * while a model is below the minimum, since that form cannot show the message by itself.
   * @param {string} text
   */
  #fillApp(text) {
    const matches = /productos/i;
    const blocked = this.#rows.some((row) => this.#dozensOf(row) < this.#minDozens);

    for (const root of this.#appRoots()) {
      const fields = /** @type {NodeListOf<HTMLInputElement | HTMLTextAreaElement>} */ (
        root.querySelectorAll('input, textarea')
      );

      for (const field of fields) {
        const label = field.id ? root.querySelector(`label[for="${CSS.escape(field.id)}"]`)?.textContent : '';
        const haystack = [field.name, field.id, field.getAttribute('aria-label'), field.placeholder, label].join(' ');
        if (!matches.test(haystack)) continue;

        this.#hideField(field);
        if (field.value === text) continue;

        const prototype = field instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
        Object.getOwnPropertyDescriptor(prototype, 'value')?.set?.call(field, text);
        field.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        field.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }

      const submit = root.querySelector("button[data-testid='btn-form-submit']");
      if (submit instanceof HTMLButtonElement && submit.disabled !== blocked) submit.disabled = blocked;
    }
  }

  /**
   * Hides the field together with its label: the outermost ancestor, inside the same tree, that holds no other field
   * or button. The visitor already sees the list above, and the field has to stay optional in the form.
   * @param {HTMLElement} field
   */
  #hideField(field) {
    const { app } = this.refs;
    let target = field;
    while (target.parentElement && target.parentElement !== app) {
      const parent = target.parentElement;
      if (parent.tagName === 'FORM' || parent.querySelectorAll('input, textarea, select, button').length > 1) break;
      target = parent;
    }

    target.hidden = true;
    target.style.display = 'none';
  }

  // ---------------------------------------------------------------- storage and prefill

  #persist() {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.#rows));
    } catch {
      // Storage unavailable: the list just does not survive a reload
    }
  }

  /**
   * Loads the list from the cart link (`?items=SKU:size:pairs,…`, in pairs, turned into dozens with the minimum as the
   * floor) or from the last visit of this session.
   */
  async #restore() {
    const params = new URLSearchParams(window.location.search);
    const requested = (params.get('items') ?? '').split(',').filter(Boolean);

    // The server rendered a posted contact form: the request was sent, start over
    if (this.querySelector('[data-pc-quote-posted]')) {
      this.#rows = [];
      this.#renderList();
      return;
    }

    if (requested.length === 0) {
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? '[]');
        if (Array.isArray(stored)) {
          this.#rows = stored.filter((row) => row?.product?.variants?.length && row.doz);
        }
      } catch {
        this.#rows = [];
      }
      this.#renderList();
      return;
    }

    this.#renderList();
    for (const entry of requested) {
      const [sku = '', size = '', pairs = '0'] = entry.split(':');
      if (!sku) continue;

      try {
        const options = await this.#fetchOptions(sku, 4, false);
        const products = options.map((option) => this.#readProduct(option)).filter(Boolean);
        const product = products.find((candidate) => candidate?.sku?.toLowerCase() === sku.toLowerCase());
        if (!product) continue;

        const dozens = Math.max(this.#minDozens, Math.ceil((parseInt(pairs, 10) || 0) / this.#pairsPerDozen));
        this.#addRow(product, { dozens, size });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }
  }

  // ---------------------------------------------------------------- native form

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    const { error, submit, blocked } = this.refs;

    if (this.#rows.length === 0) {
      if (error) {
        error.textContent = this.#strings.noProducts;
        error.hidden = false;
      }
      this.#focusSearch();
      return;
    }

    // A model below the minimum: the message is already on the page, take the visitor to it
    if (this.#rows.some((row) => this.#dozensOf(row) < this.#minDozens)) {
      if (error) error.hidden = true;
      blocked.scrollIntoView({ block: 'center' });
      return;
    }

    if (error) error.hidden = true;
    if (submit) submit.disabled = true;
    this.#syncProducts();

    let response;
    try {
      response = await fetch(form.action, { method: 'POST', body: new FormData(form) });
    } catch {
      response = undefined;
    }

    if (submit) submit.disabled = false;

    // Spam check or connection verification: the visitor solves it on the regular page. The request was not accepted,
    // so nothing is sent twice
    if (response && (response.url.includes('/challenge') || response.status === 403 || response.status === 429)) {
      form.removeEventListener('submit', this.#onSubmit);
      form.submit();
      return;
    }

    // Anything else is final: resending after an accepted request would email the store twice
    const html = response ? new DOMParser().parseFromString(await response.text(), 'text/html') : undefined;
    const posted =
      response?.ok && (html?.querySelector('[data-pc-quote-posted]') || response.url.includes('contact_posted=true'));

    if (posted) {
      this.#rows = [];
      this.#renderList();
      form.reset();
      this.#showSuccess();
    } else if (error) {
      error.textContent = this.#strings.error;
      error.hidden = false;
    }
  };

  /** Replaces the fields with the confirmation and takes the focus to it. */
  #showSuccess() {
    const { success, content } = this.refs;
    if (!success) return;

    if (content) content.hidden = true;
    success.hidden = false;
    success.setAttribute('tabindex', '-1');
    success.scrollIntoView({ block: 'nearest' });
    success.focus({ preventScroll: true });
  }

  /** @param {Event} event */
  #onAgainClick = (event) => {
    const { success, content } = this.refs;
    // The confirmation came from the server (no fields on the page): the link reloads the page
    if (!content || !success) return;

    event.preventDefault();
    success.hidden = true;
    content.hidden = false;
    content.querySelector('input')?.focus();
  };
}

if (!customElements.get('pc-quote')) {
  customElements.define('pc-quote', PcQuote);
}
